import { FOLDER_COLORS } from '@platform/shared-types';
import type { FolderColor } from '@platform/shared-types';

/**
 * THE FOLDER PALETTE, RESOLVED FOR THIS (LIGHT) CHROME.
 *
 * The stored value is an id, not a hex — see `FOLDER_COLORS` in shared-types.
 * This is where an id becomes pixels, and it is the only place: the tree rail,
 * the row chip and the colour picker all read the same row, so a folder that is
 * amber in one is amber in all three.
 *
 * Every class is written out in full rather than composed from the id
 * (`bg-${c}-100` and friends), because Tailwind scans source text and would
 * emit none of them.
 */

export interface FolderPaletteEntry {
  /** What the operator picks it by. */
  label: string;
  /** The 3px rail / dot — the colour at full strength. */
  dot: string;
  /** Tinted chip background + readable text on it. */
  chip: string;
  /** Tint for the row when this folder is the selected one. */
  selected: string;
  /** The picker swatch in its unselected state. */
  swatch: string;
}

export const FOLDER_PALETTE: Record<FolderColor, FolderPaletteEntry> = {
  stone: {
    label: 'Neutral',
    dot: 'bg-stone-400',
    chip: 'bg-stone-100 text-stone-600 border-stone-200',
    selected: 'bg-stone-100 text-stone-900',
    swatch: 'bg-stone-400',
  },
  ruby: {
    label: 'Ruby',
    dot: 'bg-rose-600',
    chip: 'bg-rose-50 text-rose-700 border-rose-200',
    selected: 'bg-rose-50 text-rose-800',
    swatch: 'bg-rose-600',
  },
  amber: {
    label: 'Amber',
    dot: 'bg-amber-500',
    chip: 'bg-amber-50 text-amber-800 border-amber-200',
    selected: 'bg-amber-50 text-amber-800',
    swatch: 'bg-amber-500',
  },
  emerald: {
    label: 'Emerald',
    dot: 'bg-emerald-500',
    chip: 'bg-emerald-50 text-emerald-700 border-emerald-200',
    selected: 'bg-emerald-50 text-emerald-800',
    swatch: 'bg-emerald-500',
  },
  sky: {
    label: 'Sky',
    dot: 'bg-sky-500',
    chip: 'bg-sky-50 text-sky-700 border-sky-200',
    selected: 'bg-sky-50 text-sky-800',
    swatch: 'bg-sky-500',
  },
  violet: {
    label: 'Violet',
    dot: 'bg-violet-500',
    chip: 'bg-violet-50 text-violet-700 border-violet-200',
    selected: 'bg-violet-50 text-violet-800',
    swatch: 'bg-violet-500',
  },
  teal: {
    label: 'Teal',
    dot: 'bg-teal-500',
    chip: 'bg-teal-50 text-teal-700 border-teal-200',
    selected: 'bg-teal-50 text-teal-800',
    swatch: 'bg-teal-500',
  },
  rose: {
    label: 'Pink',
    dot: 'bg-pink-500',
    chip: 'bg-pink-50 text-pink-700 border-pink-200',
    selected: 'bg-pink-50 text-pink-800',
    swatch: 'bg-pink-500',
  },
};

/**
 * The ids, in picker order.
 *
 * Read defensively: `FOLDER_COLORS` is a RUNTIME value from the linked
 * `@platform/shared-types`, and a Vite pre-bundle built before that export
 * existed hands back `undefined` — at module scope, which white-screens the
 * page rather than failing where it is used. Falling back to the keys of the
 * table above costs nothing and keeps the picker populated. See the identical
 * guard in ./documentCategories.ts.
 */
export const FOLDER_COLOR_IDS: readonly FolderColor[] = Array.isArray(FOLDER_COLORS)
  ? FOLDER_COLORS
  : (Object.keys(FOLDER_PALETTE) as FolderColor[]);

/** Absent, or an id this build has never heard of, both read as neutral. */
export const folderPalette = (color?: string | null): FolderPaletteEntry =>
  FOLDER_PALETTE[(color as FolderColor)] ?? FOLDER_PALETTE.stone;
